import { useNav } from "@/app/features/profile/NavContext"
import ReportEditModeToggle from "@/app/features/reports/components/edit-mode-toggle"
import { useReportContext } from "@/app/features/reports/contexts/report-context"
import BusyButton from "@/components/raytd/busy-button"
import { SubPageLayout } from "@/components/raytd/sub-page-layout"
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges"
import { Suspense, useCallback, useEffect } from 'react'
import { toast } from "sonner"
import { ManagedCustomSectionContainer } from './components/custom-section-container'
import { HeaderSection } from "./components/header-section"
import { InspectedAssetsSection } from "./components/inspected-assets-section"
import { PreparedSection } from "./components/prepared-section"
import { RatingSystemSection } from "./components/rating-system-section"
import { ReportSectionProvider, useReportSectionPage } from './components/report-section-context'
import { SectionText } from './components/section-text'
import { Badge } from "@/components/ui/badge"
import { ReportViewProvider, useReportViewContext } from "@/app/features/reports/components/report-viewer-context"
import LoadingState from "@/app/features/reports/components/loading-state"
import { capitalize } from "@/lib/utils"
interface IntroductionEditorProps {
}

function IntroductionEditor({ }: IntroductionEditorProps) {
    const { report: reportData, isLoading, isEditable } = useReportContext();
    const { isSdaFormat } = useReportViewContext();
    const { sectionManager, editMode } = useReportSectionPage();
    const { sections, saveSections, isBusy, isDirty } = sectionManager;

    const reset = useCallback(() => {
        console.log('reset');
    }, []);

    const { AlertDialogComponent } = useUnsavedChanges(isDirty, reset, false);

    useEffect(() => {
        console.debug('Report data:', reportData)
    }, [reportData])

    const { setNavButtons } = useNav();

    const onSubmit = useCallback(async () => {
        console.log('submit', sections);
        try {
            await saveSections();
            toast.success('Page content saved');
        } catch (error) {
            console.error('Error saving sections:', error);
            toast.error('Error saving page content');
        }

    }, [sections]);

    useEffect(() => {
        setNavButtons(
            editMode && <BusyButton
                onClick={onSubmit}
                disabled={!isDirty}
                isBusy={isBusy}
                busyText="Saving..."
            >
                Save
            </BusyButton>
        );

        // Clean up function to reset buttons when component unmounts
        return () => setNavButtons(null);
    }, [onSubmit, isDirty, isBusy, editMode]);

    if (isLoading) {
        return <div>Loading...</div>
    }

    return (
        <SubPageLayout title="Introduction" badge={<RevisionStatusBadge />}>
            <SubPageLayout.Header>
                <ReportEditModeToggle />
            </SubPageLayout.Header>

            <HeaderSection report={reportData as any} editMode={editMode} />

            <SectionText
                position="introduction-header"
                className="my-4"
            />

            <ManagedCustomSectionContainer
                position="introduction-after-header"
            />

            <PreparedSection
                client={reportData.client}
                authors={reportData.collaborators || []} editMode={editMode}
            />

            <SectionText
                position="introduction-prepared"
            />

            <ManagedCustomSectionContainer
                position="introduction-after-prepared"
            />

            <InspectedAssetsSection
                assets={reportData.assets as any || []}
            />

            <SectionText
                position="introduction-assets"
            />

            <div className="break-inside-avoid break-before-auto">

                <ManagedCustomSectionContainer
                    position="introduction-after-assets"
                />

            </div>

            {/* raytd/web#218 follow-up: RatingSystemSection itself already renders nothing for
                SDA, but this wrapper's `print-section-rating` class carries an unconditional
                `break-before: page` (print.css) — leaving the empty div in the tree still forced
                a blank page onto the printed SDA report, right before Results. Skip the wrapper
                entirely for SDA instead of relying on the child returning null. */}
            {!isSdaFormat && (
                <div className="print-section-rating">
                    <RatingSystemSection />
                </div>
            )}

            {/* <ManagedCustomSectionContainer
                position="introduction-after-rating"
            /> */}

            <ManagedCustomSectionContainer
                position="introduction-conclusion"
            />

            <AlertDialogComponent />

        </SubPageLayout>
    )
}


export default function Page() {

    const { report } = useReportContext();

    return (
        <ReportViewProvider viewId="default" reportId={report?.id} initialEditMode={false}>
            <ReportSectionProvider
                page="introduction"
            >

                <Suspense fallback={<LoadingState />}>
                    <IntroductionEditor />
                </Suspense>
            </ReportSectionProvider >
        </ReportViewProvider>

    )

};

function RevisionStatusBadge() {

    const { publishedReportData } = useReportContext();

    if (!publishedReportData) {
        return null;
    }

    const { revision } = publishedReportData;

    if (revision.status === 'published') {
        return null;
    }

    return (
        <Badge variant="outline" className="font-medium">
            {capitalize(revision.status)}
        </Badge>
    )
}